/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { useEffect, useMemo, useState } from 'react';
import { useKPIContext } from '@/providers/KpiProvider';
import {
  collapseNode,
  collapseSiblings,
  getDefaultNodesOrExpand,
  getNodeDepth,
  nodeSupportsView,
} from '@/utils/sankeyChartUtils';
import { useMeasure } from 'react-use';
import { SankeyData, SankeyDataNode } from 'types/sankey';

import {
  colorLinkFunc,
  colorRectFunc,
  formatLinkTitleFunc,
  formatRectTitleFunc,
  initializeColorScale,
  makeSankeyFunc,
} from './parse';
import { SankeyLabels } from './sankey-label';
import { SankeyLinks } from './sankey-link';
import { RectNode, SankeyRects } from './sankey-rect';

interface SankeyChartProps {
  data: SankeyData;
  nodeWidth: number;
  nodePadding: number;
  svgPadding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  colorPalette: string[];
  onNodeClick?: (node: SankeyDataNode) => void;
  rootNodeKey: string;
}
/**
 * A Sankey chart component that displays a Sankey diagram for a given data
 * structure. The component takes care of calculating the positions of the nodes
 * and links and rendering the chart. The component also provides a way to
 * collapse or expand the nodes by clicking on them.
 *
 * @param {SankeyData} data - The data structure to display in the Sankey chart.
 * @param {number} nodeWidth - The width of the nodes in the Sankey chart.
 * @param {number} nodePadding - The padding between the nodes in the Sankey
 *     chart.
 * @param {{top: number, right: number, bottom: number, left: number}} svgPadding
 *     - The padding around the SVG element in the Sankey chart.
 * @param {string[]} colorPalette - The colors to use for the nodes and links in
 *     the Sankey chart.
 * @param {Function} onNodeClick - A function to call when a node is clicked.
 * @param {string} rootNodeKey - The key of the root node in the Sankey chart.
 * @returns {JSX.Element} The rendered Sankey chart component.
 */
export const SankeyChart: React.FC<SankeyChartProps> = React.memo(
  ({
    data,
    nodeWidth,
    nodePadding,
    svgPadding,
    colorPalette,
    onNodeClick,
    rootNodeKey,
  }) => {
    const [ref, measurements] = useMeasure<HTMLDivElement>();
    const { viewBy } = useKPIContext();

    // Keep track of links that have already been rendered to avoid animation on every render
    const [renderedLinksCache, setRenderedLinksCache] = useState<Set<string>>(
      new Set<string>()
    );

    const { width: containerWidth } = measurements;
    // Set default height to 300px if width is not available, max is 420 coz parent has max 480 with 60px y axis padding.
    const containerHeight = Math.min(
      420,
      containerWidth > 0 ? containerWidth * 0.5 : 300
    ); // Maintain aspect ratio
    const [visibleData, setVisibleData] = useState<SankeyData>();

    // Calculate the actual chart dimensions (subtracting padding)
    const width = containerWidth - svgPadding.left - svgPadding.right;
    const height = containerHeight - svgPadding.top - svgPadding.bottom;

    useEffect(() => {
      if (!data) return;
      const visibleData = getDefaultNodesOrExpand(
        rootNodeKey,
        data,
        viewBy,
        true
      );
      if (
        !visibleData ||
        visibleData.links.length === 0 ||
        visibleData.nodes.length === 0
      )
        return;

      setVisibleData(visibleData);
      if (onNodeClick) onNodeClick(visibleData.nodes[0]);
      initializeColorScale(data, colorPalette);
    }, [data]);

    const sankeyGen = useMemo(
      () =>
        makeSankeyFunc({
          width,
          height,
          nodePadding,
          nodeWidth,
        }),
      [containerWidth, containerHeight]
    );

    const sankeyResult = useMemo(() => {
      if (
        !visibleData ||
        !Array.isArray(visibleData.links) ||
        !Array.isArray(visibleData.nodes) ||
        visibleData.links.length === 0 ||
        visibleData.nodes.length === 0
      )
        return null;

      // check all links have valid source/target
      const nodeKeys = new Set(visibleData.nodes.map((n) => n.key));
      const validLinks = visibleData.links.filter(
        (link) =>
          link.source &&
          link.target &&
          nodeKeys.has(
            typeof link.source === 'string' ? link.source : link.source.key
          ) &&
          nodeKeys.has(
            typeof link.target === 'string' ? link.target : link.target.key
          )
      );

      if (validLinks.length === 0) return null;

      try {
        // Copy data to avoid altering original data
        const cleanData = {
          nodes: [...visibleData.nodes],
          links: validLinks,
        };
        return sankeyGen(cleanData);
      } catch (error) {
        console.error('Sankey generation error:', error);
        return null;
      }
    }, [visibleData, sankeyGen]);

    // Identify nodes that have at least one child supported by the current view; used for chevron visibility
    const nodesWithChildren = useMemo(() => {
      const parents = new Set<string>();
      data.nodes.forEach((node) => {
        // check if any descendant node supports current view
        const hasDescendantSupportingView = data.nodes.some(
          (n) => n.key.startsWith(node.key + '.') && nodeSupportsView(n, viewBy)
        );
        // show chevron if node or any descendants supports current view
        if (
          node.parentKey &&
          (!viewBy ||
            nodeSupportsView(node, viewBy) ||
            hasDescendantSupportingView)
        ) {
          parents.add(node.parentKey);
        }
      });
      return parents;
    }, [data.nodes, viewBy]);

    if (!visibleData || !sankeyResult) return null;

    const handleNodeClickAndUpdate = (
      e: React.MouseEvent<SVGRectElement, MouseEvent>,
      node: RectNode
    ) => {
      e.stopPropagation();
      if (onNodeClick) onNodeClick(node);
      if (node.key === rootNodeKey) return; //make sure you always have the correct root node key.
      const isNotALeafNode = visibleData.links.some(
        (link) => link.source.key === node.key
      );
      const isExpandable = nodesWithChildren.has(node.key);

      if (isNotALeafNode) {
        // on collapse, hide children of this node
        setVisibleData(
          collapseNode(
            node.key,
            setRenderedLinksCache,
            data,
            renderedLinksCache,
            viewBy
          )
        );
      } else if (isExpandable) {
        // ensure only one node is expanded at root + 3 or deeper levels
        if (getNodeDepth(node, rootNodeKey, data.nodes) >= 3)
          collapseSiblings(node.key, data, rootNodeKey);
        setVisibleData(getDefaultNodesOrExpand(node.key, data, viewBy));
      }
    };

    const { nodes, links } = sankeyResult;
    return (
      // ResizeObserver doesn't work directly on <svg/>
      <div ref={ref} className="h-full w-full">
        {width > 0 && (
          <>
            <svg
              id="sankey-chart-svg"
              viewBox={`0 0 ${containerWidth} ${containerHeight}`}
            >
              <g
                transform={`translate(${svgPadding.left}, ${svgPadding.top})`}
                style={{ width: `${width}` }}
              >
                <SankeyRects
                  nodes={nodes}
                  colorFunc={colorRectFunc}
                  titleFunc={formatRectTitleFunc}
                  links={links}
                  handleNodeClick={handleNodeClickAndUpdate}
                />
                <SankeyLinks
                  links={links}
                  colorFunc={colorLinkFunc}
                  titleFunc={formatLinkTitleFunc}
                  renderedLinksCache={renderedLinksCache}
                  setRenderedLinksCache={setRenderedLinksCache}
                />
                <SankeyLabels
                  nodes={nodes}
                  handleNodeClick={handleNodeClickAndUpdate}
                  nodesWithChildren={nodesWithChildren}
                  rootNodeKey={rootNodeKey}
                />
              </g>
            </svg>
            <div
              id="tooltip"
              className="pointer-events-none absolute rounded border bg-white p-2 text-sm shadow-lg"
              style={{
                opacity: 0,
                transition: 'opacity 0.3s',
                pointerEvents: 'none',
                position: 'absolute',
              }}
            ></div>
          </>
        )}
      </div>
    );
  }
);
SankeyChart.displayName = 'SankeyChart';
